import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  type Chatbox,
  type CustomFieldPredicate,
  type SendMessageEvent,
  type Session,
} from 'talkjs/all';
import { Box } from '@rsos/components/capstone/base';
import { ACCEPTED } from '@rsos/constants/alertStatuses';
import { CONVERSATION_ENDED } from '@rsos/constants/chatStatuses';
import useLoggedInUser from '@rsos/hooks/useLoggedInUser';
import { useCurrentConversation } from '@rsos/reduxDataStore/src/features/alerts/hooks';
import { addFollower } from '@rsos/reduxDataStore/src/features/alerts/incidentsSlice';
import { useSelectedAlert } from '@rsos/reduxDataStore/src/features/selectors/alertsSelectors';
import { callQueueSelector } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import checkAniAliAccessSelector from '@rsos/utils/checkAniAliAccessSelector';
import {
  handleReadAlert,
  handleUnreadAlerts,
} from '@rsos/utils/handleReadUnreadAlerts';
import { captureException } from '@rsos/utils/sentry';
import {
  createTalkSession,
  createTalkUser,
  getChatPermissions,
  getChatFilterPermissions,
} from '@rsos/utils/talkJS';
import { RootState } from '../../app';
import useIsCallAutomationAlert from '../../hooks/useIsCallAutomationAlert';
import useTalkJSTheme from '../../hooks/useTalkJSTheme';
import {
  handleReadCall,
  handleUnreadCalls,
} from '../../utils/handleReadUnreadCalls';

const ALERT_ID_PREFIX = 'alert-';
const ALERT_CONVERSATION_ID_PREFIX = 'chat-portal-default-';

/**
 * TalkJS message field visibility rules:
 * Show:
 * - alert status is ACCEPTED and Conversation Status is not CONVERSATION_ENDED
 * hide:
 * - alert is supplemental only
 * - alert status is NEW, DECLINED, TIMEOUT, CANCELED, IGNORED
 * - conversation status is CONVERSATION_ENDED
 * Do not check isChatEnabled. It is not used for Portal alerts, only iCSP
 * @param {String} alertStatus - alert status
 * @param {String} conversationStatus - alert conversation status
 * @param {Boolean} isCallAutomationAlert - Is the alert a call automation alert
 * @returns Boolean
 */
const checkMessageFieldVisibility = ({
  alertStatus = '',
  conversationStatus = '',
  isCallAutomationAlert = false,
}) => {
  return (
    alertStatus === ACCEPTED &&
    !!conversationStatus &&
    conversationStatus !== CONVERSATION_ENDED &&
    !isCallAutomationAlert
  );
};

/**
 * Create a TalkJS chat iframe container for alerts, when an alert status is accepted, the user can
 * participate in a conversation with other users in RSP and CSP.
 */
const TalkJSChatbox = () => {
  const dispatch = useDispatch();

  const chatTheme = useTalkJSTheme();

  const profile = useSelector((state: RootState) => state.sinatra.user.profile);

  const {
    account_id: currentPSAPAccountID,
    display_name: currentPSAPDisplayName,
  } = useSelector((state: RootState) => state.psaps.currentPSAP);

  const hasAniAliAccess = useSelector((state: RootState) =>
    checkAniAliAccessSelector({ psaps: state.psaps, sinatra: state.sinatra }),
  );
  const filteredCalls = useSelector((state: RootState) =>
    callQueueSelector(state.emergencyCalls.callQueue, hasAniAliAccess),
  );

  const selectedCallerID = useSelector(
    (state: RootState) => state.emergencyCalls.selectedCallerID,
  );

  const callConversationID = selectedCallerID
    ? filteredCalls?.[selectedCallerID]?.agencyShareChat?.conversationID
    : null;

  const isCallReceived = selectedCallerID
    ? filteredCalls?.[selectedCallerID]?.agencyShareChat?.isCallReceived
    : false;

  const isCallShared = selectedCallerID
    ? filteredCalls?.[selectedCallerID]?.agencyShareChat?.isCallShared
    : false;

  const alertsQueue = useSelector((state: RootState) => state.incidents.alerts);

  const selectedAlertID = useSelector(
    (state: RootState) => state.incidents.selectedAlertID,
  );

  const latestMsgTime = useSelector(
    (state: RootState) => state.incidents.latestMsgTime,
  );

  const currentAlert = useSelectedAlert();

  const alertStatus = currentAlert?.status?.name;

  const alertConversation = useCurrentConversation();

  const alertConversationID = alertConversation?.conversation?.id;

  const alertConversationStatus = alertConversation?.conversation?.status;

  const isCallAutomationAlert = useIsCallAutomationAlert(selectedAlertID);

  // TalkJS chat configuration
  const loggedInUser = useLoggedInUser(
    selectedAlertID ? 'rsp_alerts_user' : 'rsp_calls_user',
  );

  const [chatSession, setChatSession] = useState<Session | null>(null);
  const [chatbox, setChatbox] = useState<Chatbox | null>(null);
  const [openedAlerts, setOpenedAlerts] = useState(new Set());
  const [openedCalls, setOpenedCalls] = useState(new Set());

  const chatRef = useRef<HTMLDivElement>(null);

  /**
   * TalkJS chatbox on change event handler for alerts
   * https://talkjs.com/docs/Reference/JavaScript_Chat_SDK/Chatbox.html#Chatbox.on__sendMessage__
   */
  const handleSendMessage = useCallback(
    (event: SendMessageEvent) => {
      const permissions = getChatPermissions();

      event.override({
        custom: {
          entity_display_name: currentPSAPDisplayName || '',
          entity_id: currentPSAPAccountID || '',
          from_app: 'RSP',
          sender: loggedInUser.name,
          ...(selectedAlertID && {
            selectedAlertID,
            ...permissions,
          }),
          ...(selectedCallerID && { selectedCallerID }),
        },
        text: event.message.text,
      });

      if (selectedAlertID && profile?.id) dispatch(addFollower(profile.id));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedAlertID, selectedCallerID],
  );

  /**
   * =========================================
   *  TALKJS CHATBOX & UNREADS INITIALIZATION
   * =========================================
   */

  // Create and mount the TalkJS session and chat container UI when the AlertPane is opened.
  // The TalkJS session and chat contaienr UI is destroyed when the AlertPane is closed.
  // https://talkjs.com/docs/Reference/JavaScript_Chat_SDK/Session/#Session__createChatbox
  // https://talkjs.com/docs/Reference/JavaScript_Chat_SDK/Chatbox/#Chatbox__mount
  useEffect(() => {
    let session: Session | undefined, chat: Chatbox | undefined;

    try {
      (async () => {
        session = await createTalkSession(loggedInUser);

        chat = session?.createChatbox({
          theme: chatTheme,
          showChatHeader: false,
          messageField: {
            visible: false,
            placeholder: 'Type response',
          },
        });

        chat?.mount(chatRef.current);

        setChatSession(session || null);

        setChatbox(chat || null);
      })();
    } catch (error) {
      captureException(error);
    }

    return () => {
      if (session) {
        setChatbox(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatTheme]);

  // Track unread alerts and 911 events while the pane is open.
  useEffect(() => {
    if (chatbox && (selectedAlertID || selectedCallerID)) {
      handleUnreadAlerts(
        chatSession,
        alertsQueue,
        selectedAlertID,
        latestMsgTime,
        dispatch,
      );

      handleUnreadCalls(chatSession, filteredCalls, selectedCallerID, dispatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatbox, dispatch, selectedAlertID, selectedCallerID]);

  /**
   * ========================
   *  ALERTS USEEFFECT HOOKS
   * ========================
   */

  // When switching between different alerts while the AlertPane is open, the TalkJS session and
  // chat container UI stays. The conversation is updated using the select() method and checks for
  // the message field visibility using the checkMessageFieldVisibility() util function.
  // Additionally, the selectedAlertID suffix and alertConversationID suffix must match so that
  // TalkJS can select the correction alert conversation from the backend.
  // https://talkjs.com/docs/Reference/JavaScript_Chat_SDK/Session/#Session__getOrCreateConversation
  // https://talkjs.com/docs/Reference/JavaScript_Chat_SDK/Chatbox/#Chatbox__select
  useEffect(() => {
    if (
      chatbox &&
      chatSession &&
      selectedAlertID &&
      alertConversationID &&
      selectedAlertID.split(ALERT_ID_PREFIX)[1] ===
        alertConversationID.split(ALERT_CONVERSATION_ID_PREFIX)[1]
    ) {
      const conversation =
        chatSession.getOrCreateConversation(alertConversationID);

      conversation.setParticipant(createTalkUser(loggedInUser));

      chatbox.select(conversation);

      const isMessageFieldVisible = checkMessageFieldVisibility({
        alertStatus,
        conversationStatus: alertConversationStatus,
        isCallAutomationAlert: isCallAutomationAlert,
      });

      chatbox.messageField.setVisible(isMessageFieldVisible);

      const customFilter = getChatFilterPermissions() as unknown as Record<
        string,
        CustomFieldPredicate
      >;

      chatbox.setMessageFilter({
        custom: {
          ...customFilter,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    alertConversationID,
    alertConversationStatus,
    alertStatus,
    chatbox,
    chatSession,
    selectedAlertID,
  ]);

  // When onSendMessage is called it will stack the functions instead of overwritting them
  // To fix this, moving it into it's own useEffect should ensure that it gets called only once
  useEffect(() => {
    if (
      chatbox &&
      chatSession &&
      chatTheme &&
      !openedAlerts.has(`${selectedAlertID}-${chatTheme}`) &&
      selectedAlertID &&
      alertConversationID &&
      selectedAlertID.split(ALERT_ID_PREFIX)[1] ===
        alertConversationID.split(ALERT_CONVERSATION_ID_PREFIX)[1]
    ) {
      setOpenedAlerts(
        new Set(openedAlerts.add(`${selectedAlertID}-${chatTheme}`)),
      );

      chatbox.onSendMessage(event => {
        if (alertConversationID === event.conversation.id) {
          handleSendMessage(event);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    alertConversationID,
    chatbox,
    chatSession,
    chatTheme,
    handleSendMessage,
    openedAlerts,
    selectedAlertID,
    selectedCallerID,
  ]);

  useEffect(() => {
    if (
      chatbox &&
      chatSession &&
      chatTheme &&
      !openedCalls.has(`${selectedCallerID}-${chatTheme}`) &&
      selectedCallerID &&
      callConversationID &&
      (isCallShared || isCallReceived)
    ) {
      setOpenedCalls(
        new Set(openedCalls.add(`${selectedCallerID}-${chatTheme}`)),
      );

      chatbox.onSendMessage(event => {
        if (callConversationID === event.conversation.id) {
          handleSendMessage(event);
        }
      });
    }
  }, [
    callConversationID,
    chatSession,
    chatbox,
    chatTheme,
    handleSendMessage,
    isCallReceived,
    isCallShared,
    openedCalls,
    selectedCallerID,
  ]);

  // Reset openedAlerts to a new set when the chatbox changes, this could happen when the user
  // selects a new alert or call or changes the theme.
  useEffect(() => {
    if (chatTheme && openedAlerts.size > 0) {
      setOpenedAlerts(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatTheme]);

  // Set the alert conversation as read when it is selected.
  useEffect(() => {
    if (selectedAlertID) handleReadAlert(selectedAlertID, dispatch);
  }, [dispatch, selectedAlertID]);

  // Set the message field visibility to false if it is a call automation alert.
  useEffect(() => {
    if (chatbox && chatSession && selectedAlertID && isCallAutomationAlert) {
      chatbox.messageField.setVisible(!isCallAutomationAlert);
    }
  }, [chatbox, chatSession, selectedAlertID, isCallAutomationAlert]);

  /**
   * ============================
   *  911 EVENTS USEEFFECT HOOKS
   * ============================
   */

  // When switching between different 911 events while the CallPane is open, the TalkJS session and
  // chat container UI stays. The conversation is updated using the select() method.
  // Additionally, the 911 event should have a callConversationID and it should be shared or
  // received by peer agencies.
  useEffect(() => {
    if (
      chatbox &&
      chatSession &&
      selectedCallerID &&
      callConversationID &&
      (isCallShared || isCallReceived)
    ) {
      const conversation =
        chatSession.getOrCreateConversation(callConversationID);

      conversation.setParticipant(createTalkUser(loggedInUser));

      chatbox.select(conversation);

      chatbox.messageField.setVisible(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    callConversationID,
    chatbox,
    chatSession,
    isCallReceived,
    isCallShared,
    selectedCallerID,
  ]);

  // Reset openedCalls to a new set when the chatbox changes, this could happen when the user
  // selects a new alert or call or changes the theme.
  useEffect(() => {
    if (chatTheme && openedCalls.size > 0) {
      setOpenedCalls(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatTheme]);

  // Set the 911 event conversation as read when it is selected.
  useEffect(() => {
    if (selectedCallerID) handleReadCall(selectedCallerID, dispatch);
  }, [dispatch, selectedCallerID]);

  return <Box width="100%" height="100%" ref={chatRef}></Box>;
};

export default TalkJSChatbox;
